import { useQuery } from '@tanstack/react-query';
import { publicClient } from '@/api/publicClient';
import type { components } from '@/api/generated/schema';

export type PublicInvoice = components['schemas']['PublicInvoiceResponse'];
export type PublicInvoiceDetail = components['schemas']['PublicInvoiceDetail'];

/**
 * A 404 from the invoice link: malformed, revoked, unknown, a draft, a void
 * more than 90 days old, a shop no longer active. The API makes every one of
 * those the same answer on purpose, so the page does too — one "no longer
 * valid" page that names no shop.
 */
export class PublicInvoiceGoneError extends Error {
  constructor() {
    super('This link is no longer valid');
    this.name = 'PublicInvoiceGoneError';
  }
}

/**
 * The customer's invoice, read through its link (M2-16) with no sign-in —
 * through `publicClient`, never `apiClient` (see that file for why: no bearer
 * token, and no 401 can send a customer to the office's sign-in page).
 *
 * Not refetched on focus — the customer is reading a document — and no retry
 * on a 404, which is an answer.
 */
export function usePublicInvoice(token: string | undefined) {
  return useQuery({
    enabled: token !== undefined,
    queryKey: ['public-invoice', token] as const,
    queryFn: async () => {
      const { data, response } = await publicClient.GET('/api/v1/public/invoices/{token}', {
        params: { path: { token: token! } },
      });
      if (response.status === 404) throw new PublicInvoiceGoneError();
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/public/invoices/{token} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}
